import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { ReactFlowInstance } from '@xyflow/react';
import { AxiosResponse } from 'axios';
import { castDraft } from 'immer';
import { cloneDeep, get, isEmpty, isEqual, merge, set } from 'lodash';
import { handleErrorNotification } from '@utils/error';
import {
  generateIntegratedPipelineYamlApi,
  getPipelineSchemaApi,
  getPipelineSchemaInputOutputApi,
} from '@api/pipeline';
import { StatusCodes } from '@constants/enum/common';
import { PipelineMessages } from '@constants/pipelines';
import { addNotification } from '@redux/actions/notificationActions';
import { RootState } from '@redux/store';
import {
  ApiResponse,
  ComponentPosition,
  NotificationType,
  PipelineVisualizationMetadata,
} from '@redux/types/types';
import { getYamlString } from '../logic/generateYaml';
import { getComponentMappings, getInputOutputMappings } from '../logic/transformSchema';
import { HistoryItem, IComponentMapping } from '../logic/types';

interface StudioState {
  pipelineSchema: Record<string, any> | null;
  haystackTypes: Record<string, any> | {};
  componentMap: IComponentMapping[];
  fetchPipelineSchemaStatus: StatusCodes;
  inputOutputMap: Record<string, any>;
  fetchPipelineInputOutputsStatus: StatusCodes;
  exportCodeModalOpen: boolean;
  isYamlView: boolean;
  yaml: string;
  fetchedSingleIntegratedPipelineYaml: StatusCodes;
  isReadOnly: boolean;
  showReadOnlyWarningModal: boolean;
  hoveredNodeId: string;
  reactFlowInstance: ReactFlowInstance | null;
  pastIndexingReactFlowState: HistoryItem[];
  futureIndexingReactFlowState: HistoryItem[];
  pastQueryReactFlowState: HistoryItem[];
  futureQueryReactFlowState: HistoryItem[];
  newNodeAdded: boolean;
  nodeParamModalVisible: { nodeId: string; paramName: string };
  documentStoreComponents: Record<string, any>;
  disconnectedDocumentStores: string[];
  saveStudioPipelineStatus: StatusCodes;
  isIndexPipeline: boolean;
  componentPositions: Record<string, ComponentPosition>;
  fetchPipelineVisualizationDataStatus: StatusCodes;
}

export const initialState: StudioState = {
  pipelineSchema: {},
  haystackTypes: {},
  componentMap: [],
  fetchPipelineSchemaStatus: StatusCodes.IDLE,
  inputOutputMap: {},
  fetchPipelineInputOutputsStatus: StatusCodes.IDLE,
  exportCodeModalOpen: false,
  isYamlView: false,
  yaml: '',
  fetchedSingleIntegratedPipelineYaml: StatusCodes.IDLE,
  isReadOnly: false,
  showReadOnlyWarningModal: false,
  hoveredNodeId: '',
  reactFlowInstance: null,
  pastIndexingReactFlowState: [],
  futureIndexingReactFlowState: [],
  pastQueryReactFlowState: [],
  futureQueryReactFlowState: [],
  newNodeAdded: false,
  nodeParamModalVisible: { nodeId: '', paramName: '' },
  documentStoreComponents: {},
  disconnectedDocumentStores: [],
  saveStudioPipelineStatus: StatusCodes.IDLE,
  isIndexPipeline: false,
  componentPositions: {},
  fetchPipelineVisualizationDataStatus: StatusCodes.IDLE,
};

export const fetchPipelineSchema = createAsyncThunk('studio/fetchPipelineSchema', async () => {
  const response = await getPipelineSchemaApi();
  return response.data.component_schema;
});

// TODO: Move to utils/logic
const getAllKeys = (schema: any, keyFilter: string) => {
  /**
   * Method to get all keys exactly matching the keyFilter
   */
  const keys: string[] = [];
  const traverse = (obj: any) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (key.includes(keyFilter)) {
          keys.push(obj.$ref);
        }
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          traverse(obj[key]);
        }
      }
    }
  };
  traverse(schema);
  return keys;
};

const cleanUnusedDefinitions = (schema: any) => {
  /**
   * We can drop all unused definitions within a schema (path suffix #/definitions)
   * That are not used within the input/output schema. Therefore we
   *    1. Go through all (nested) Properties and get a list of all keys
   *    2. We filter the definitions with the list of keys
   *    3. We add all the definitions to the returned schema
   *    4. We repeat this process until we have no more missing definitions
   *
   * Step 4 is important since the definitions themselfes can again have references
   * to other definitions.
   *
   * This will make suggesting, validating and memory usage significantly better!
   */
  let missingDefs = getAllKeys(schema, '$ref');
  // Keys look like #/definitions/haystackTypes/myType, ...
  // We parse the key to access only the path in the definitions object via
  // JSON sub paths
  const filteredDefinitions = { definitions: {} };
  const allDefinitions = schema;
  while (missingDefs.length > 0) {
    for (const key of missingDefs) {
      const jsonPath = key.replace(/^#\//, '').split('/');
      const value = get(allDefinitions, jsonPath);
      if (value) {
        set(filteredDefinitions, jsonPath, value);
      }
    }
    missingDefs = getAllKeys(filteredDefinitions, '$ref').filter(
      (key) => !get(filteredDefinitions, key.replace(/^#\//, '').split('/')),
    );
  }
  return { ...schema, definitions: filteredDefinitions.definitions };
};

const getDocumentStoreSchema = (documentStoreParam: any, pipelineSchema: any) => {
  /**
   * Generates a document store schema from scratch.
   * This reflects the schema for a component with an input/output of only a single document store.
   */
  const schema = {
    type: 'object',
    properties: {
      document_store: {
        ...documentStoreParam,
      },
    },
    required: ['document_store'],
    definitions: { haystackTypes: pipelineSchema.data.component_schema.definitions.haystackTypes },
  };

  return cleanUnusedDefinitions(schema);
};

const deepMergeSchema = (left: any, right: any) => {
  return merge({}, left, right);
};

const overwriteInputOutput = (
  componentName: string,
  componentSchema: any,
  allInputOutput: any,
  pipelineSchema: any,
) => {
  const initParams = componentSchema?.properties?.init_parameters;
  const documentStoreParam = initParams?.properties?.document_store;

  // Check if the document_store is a native haystack document store
  // this is a workaround for custom components that have a document store as an init parameter.
  // Our document stores are extracted in the schema via haystack types.
  // For custom components, we don't have that => fallback to old implementation
  const isNativeHaystackDocstore =
    initParams?.properties?.document_store?.$ref?.includes('#/definitions/haystackTypes') ||
    initParams?.properties?.document_store?.anyOf?.some((item: any) =>
      item?.$ref?.includes('#/definitions/haystackTypes'),
    ); // TODO: We definitely need to make this more robust. We can't rely on the schema format to render the docstore. Let's add a key `native_haystack_docstore` to the component schema.

  if (!documentStoreParam || !isNativeHaystackDocstore) return;
  const docstoreSchema = getDocumentStoreSchema(documentStoreParam, pipelineSchema);

  const existingIO = allInputOutput.find((io: any) => io.name === componentName);
  if (!existingIO) {
    allInputOutput.push({
      name: componentName,
      input: docstoreSchema,
      output: {
        type: 'object',
        properties: {},
        required: [],
        additionalProperties: false,
      },
    });
    return;
  }
  existingIO.input = cleanUnusedDefinitions(deepMergeSchema(existingIO.input, docstoreSchema));
  existingIO.output = cleanUnusedDefinitions(existingIO.output);
};

export const fetchPipelineInputOutputs = createAsyncThunk(
  'studio/fetchPipelineInputOutputs',
  async () => {
    const pipelineSchema = await getPipelineSchemaApi();
    const response = await getPipelineSchemaInputOutputApi();
    let allInputOutput = response.data;

    // Check all Components for document_store init parameter
    const components = pipelineSchema.data.component_schema.definitions.Components;
    Object.entries(components).forEach(([componentName, componentSchema]: [string, any]) => {
      overwriteInputOutput(componentName, componentSchema, allInputOutput, pipelineSchema);
    });
    // Add document store outputs and check for document_store init parameters
    const documentStores = Object.entries(
      pipelineSchema.data.component_schema.definitions.haystackTypes,
    ).filter(([key]) => key.includes('DocumentStore'));
    documentStores.forEach(([name]) => {
      if (!allInputOutput.find((io: any) => io.name === name)) {
        allInputOutput.push({
          name,
          input: {
            type: 'object',
            properties: {},
            required: [],
            additionalProperties: false,
          },
          output: getDocumentStoreSchema(
            { $ref: `#/definitions/haystackTypes/${name}`, type: 'object' },
            pipelineSchema,
          ),
        });
      }
    });
    return allInputOutput;
  },
);

export const generateIntegratedPipelineYaml = createAsyncThunk(
  'studio/generateIntegratedPipelineYaml',
  async (payload: { original: string; updated: Record<string, unknown> }) => {
    const response = await generateIntegratedPipelineYamlApi(payload);
    return response.data;
  },
);

export const saveStudioPipeline = createAsyncThunk(
  'studio/saveStudioPipeline',
  async (
    {
      apiCall,
      flowGraphPayload,
      successMessage = PipelineMessages.SAVE_SUCCESS,
    }: {
      apiCall: (yaml: string) => Promise<AxiosResponse>;
      flowGraphPayload: { original: string; updated: Record<string, unknown> };
      successMessage?: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const { isYamlView, yaml } = (getState() as RootState).studioStore;

    try {
      let yamlToSave = yaml;
      if (!isYamlView) {
        const { data: yamlFromGraph } = await generateIntegratedPipelineYamlApi(flowGraphPayload);
        yamlToSave = yamlFromGraph;
      }

      await apiCall(yamlToSave);

      dispatch(
        addNotification({
          content: successMessage,
          type: NotificationType.Success,
        }),
      );
      return yamlToSave;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const fetchPipelineVisualizationData = createAsyncThunk(
  'studio/fetchPipelineVisualizationData',
  async ({ apiCall }: { apiCall: () => ApiResponse<PipelineVisualizationMetadata> }) => {
    const { data } = await apiCall();
    return data;
  },
);

export const savePipelineVisualizationData = createAsyncThunk(
  'studio/savePipelineVisualizationData',
  async ({ apiCall }: { apiCall: () => ApiResponse<PipelineVisualizationMetadata> }) => {
    const { data } = await apiCall();
    return data;
  },
);

export const studioSlice = createSlice({
  name: 'studio',
  initialState,
  reducers: {
    resetStudioState: (state) => {
      const { pipelineSchema, haystackTypes, componentMap, inputOutputMap } = state;
      Object.assign(state, {
        ...initialState,
        pipelineSchema,
        haystackTypes,
        componentMap,
        inputOutputMap,
      });
    },
    setExportCodeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.exportCodeModalOpen = action.payload;
    },
    setIsYamlView: (state, action: PayloadAction<boolean>) => {
      state.isYamlView = action.payload;
    },
    setYaml: (state, action: PayloadAction<string>) => {
      state.yaml = action.payload;
    },
    setComponentMap: (state, action: PayloadAction<IComponentMapping[]>) => {
      state.componentMap = action.payload;
    },
    setNodesInputOutput: (state, action: PayloadAction<Record<string, any>>) => {
      state.inputOutputMap = action.payload;
    },
    updateInputOutputMap: (state, action: PayloadAction<Record<string, any>>) => {
      const hasNewValues = Object.keys(action.payload).some(
        (key) => !isEqual(state.inputOutputMap[key], action.payload[key]),
      );

      if (hasNewValues) {
        state.inputOutputMap = {
          ...state.inputOutputMap,
          ...action.payload,
        };
      }
    },
    setIsReadOnly: (state, action: PayloadAction<boolean>) => {
      state.isReadOnly = action.payload;
    },
    setShowReadOnlyWarningModal: (state, action: PayloadAction<boolean>) => {
      state.showReadOnlyWarningModal = action.payload;
    },
    setHoveredNodeId: (state, action: PayloadAction<string>) => {
      state.hoveredNodeId = action.payload;
    },
    setReactFlowInstance: (state, action: PayloadAction<ReactFlowInstance>) => {
      state.reactFlowInstance = action.payload;
    },
    setComponentPositions: (state, action: PayloadAction<Record<string, ComponentPosition>>) => {
      state.componentPositions = action.payload;
    },
    setPastIndexingReactFlowState: (state, action: PayloadAction<HistoryItem[]>) => {
      const hasChanged = !isEqual(action.payload, state.pastIndexingReactFlowState);
      if (hasChanged) {
        const updatedPast = cloneDeep(action.payload);
        state.pastIndexingReactFlowState = castDraft(updatedPast);
      }
    },
    setFutureIndexingReactFlowState: (state, action: PayloadAction<HistoryItem[]>) => {
      const hasChanged = !isEqual(action.payload, state.futureIndexingReactFlowState);
      if (hasChanged) {
        const updatedFuture = cloneDeep(action.payload);
        state.futureIndexingReactFlowState = castDraft(updatedFuture);
      }
    },
    setPastQueryReactFlowState: (state, action: PayloadAction<HistoryItem[]>) => {
      const hasChanged = !isEqual(action.payload, state.pastQueryReactFlowState);
      if (hasChanged) {
        const updatedPast = cloneDeep(action.payload);
        state.pastQueryReactFlowState = castDraft(updatedPast);
      }
    },
    setFutureQueryReactFlowState: (state, action: PayloadAction<HistoryItem[]>) => {
      const hasChanged = !isEqual(action.payload, state.futureQueryReactFlowState);
      if (hasChanged) {
        const updatedFuture = cloneDeep(action.payload);
        state.futureQueryReactFlowState = castDraft(updatedFuture);
      }
    },
    setNewNodeAdded: (state, action: PayloadAction<boolean>) => {
      state.newNodeAdded = action.payload;
    },
    setNodeParamModalVisible: (
      state,
      action: PayloadAction<{ nodeId: string; paramName: string }>,
    ) => {
      state.nodeParamModalVisible = action.payload;
    },
    setDisconnectedDocumentStores: (state, action: PayloadAction<string[]>) => {
      state.disconnectedDocumentStores = action.payload;
    },
    setIsIndexPipeline: (state, action: PayloadAction<boolean>) => {
      state.isIndexPipeline = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPipelineSchema.pending, (state) => {
      state.fetchPipelineSchemaStatus = StatusCodes.IN_PROGRESS;
    });
    builder.addCase(fetchPipelineSchema.fulfilled, (state, action) => {
      state.fetchPipelineSchemaStatus = StatusCodes.SUCCESS;

      if (action.payload) {
        const schema = action.payload;
        // Remove document_store init parameter from components
        Object.values(schema.definitions.Components).forEach((component: any) => {
          if (
            component?.properties?.init_parameters?.properties?.document_store &&
            !component?.properties?.type?.const?.includes('deepset_cloud_custom_nodes') &&
            !component?.properties?.type?.const?.includes('dc_custom_component')
          ) {
            delete component.properties.init_parameters.properties.document_store;
            // Remove from required array if present
            const required = component.properties.init_parameters.required;
            if (required) {
              const index = required.indexOf('document_store');
              if (index > -1) {
                required.splice(index, 1);
              }
            }
          }
        });

        const documentStoreComponents = Object.entries(schema.definitions.haystackTypes)
          .filter(([key]) => key.includes('DocumentStore'))
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        Object.entries(documentStoreComponents).forEach(([, value]: [string, any]) => {
          if (value?.properties?.init_parameters?.properties?.index) {
            value.properties.init_parameters.required.push('index');
            value.properties.init_parameters.properties.index = {
              ...value.properties.init_parameters.properties.index,
              type: 'string',
              enum: [],
              default: '',
            };
          }
        });

        schema.definitions.Components = {
          ...schema.definitions.Components,
          ...documentStoreComponents,
        };

        const components = schema.definitions?.Components || {};
        state.haystackTypes = schema.definitions?.haystackTypes || {};
        state.pipelineSchema = components;
        state.documentStoreComponents = documentStoreComponents;
        state.componentMap = getComponentMappings(components);
      }
    });
    builder.addCase(fetchPipelineSchema.rejected, (state) => {
      state.fetchPipelineSchemaStatus = StatusCodes.ERROR;
    });

    builder.addCase(fetchPipelineInputOutputs.pending, (state) => {
      state.fetchPipelineInputOutputsStatus = StatusCodes.IN_PROGRESS;
    });
    builder.addCase(fetchPipelineInputOutputs.fulfilled, (state, action) => {
      state.fetchPipelineInputOutputsStatus = StatusCodes.SUCCESS;
      if (action.payload) {
        state.inputOutputMap = getInputOutputMappings(action.payload);
      }
    });
    builder.addCase(fetchPipelineInputOutputs.rejected, (state) => {
      state.fetchPipelineInputOutputsStatus = StatusCodes.ERROR;
    });

    builder.addCase(generateIntegratedPipelineYaml.pending, (state) => {
      state.fetchedSingleIntegratedPipelineYaml = StatusCodes.IN_PROGRESS;
    });
    builder.addCase(generateIntegratedPipelineYaml.fulfilled, (state, action) => {
      state.yaml = action.payload;
      state.fetchedSingleIntegratedPipelineYaml = StatusCodes.SUCCESS;
    });
    builder.addCase(generateIntegratedPipelineYaml.rejected, (state, action) => {
      const yamlString = !isEmpty(action.meta.arg.updated)
        ? getYamlString(action.meta.arg.updated)
        : action.meta.arg.original;
      state.yaml = yamlString;
      state.fetchedSingleIntegratedPipelineYaml = StatusCodes.ERROR;
    });

    builder.addCase(saveStudioPipeline.pending, (state) => {
      state.saveStudioPipelineStatus = StatusCodes.IN_PROGRESS;
    });
    builder.addCase(saveStudioPipeline.fulfilled, (state, action) => {
      state.saveStudioPipelineStatus = StatusCodes.SUCCESS;
      state.yaml = action.payload;
    });
    builder.addCase(saveStudioPipeline.rejected, (state) => {
      state.saveStudioPipelineStatus = StatusCodes.ERROR;
    });

    builder.addCase(fetchPipelineVisualizationData.pending, (state) => {
      state.fetchPipelineVisualizationDataStatus = StatusCodes.IN_PROGRESS;
    });
    builder.addCase(fetchPipelineVisualizationData.fulfilled, (state, action) => {
      const { components } = action.payload ?? {};
      const componentPositions = Object.entries(components).reduce((acc, [key, value]) => {
        const { position } = value;
        if (!position) return acc;
        return {
          ...acc,
          [key]: {
            x: value.position.x,
            y: value.position.y,
          },
        };
      }, {});

      state.fetchPipelineVisualizationDataStatus = StatusCodes.SUCCESS;
      state.componentPositions = componentPositions;
    });
    builder.addCase(fetchPipelineVisualizationData.rejected, (state) => {
      state.fetchPipelineVisualizationDataStatus = StatusCodes.ERROR;
    });
  },
});

export const {
  resetStudioState,
  setIsYamlView,
  setExportCodeModalOpen,
  setYaml,
  setNodesInputOutput,
  setComponentMap,
  updateInputOutputMap,
  setIsReadOnly,
  setComponentPositions,
  setShowReadOnlyWarningModal,
  setHoveredNodeId,
  setReactFlowInstance,
  setPastIndexingReactFlowState,
  setFutureIndexingReactFlowState,
  setPastQueryReactFlowState,
  setFutureQueryReactFlowState,
  setNewNodeAdded,
  setNodeParamModalVisible,
  setDisconnectedDocumentStores,
  setIsIndexPipeline,
} = studioSlice.actions;

export default studioSlice.reducer;

// SELECTORS
const selectStudioState = (state: RootState) => state.studioStore;

const getPipelineSchema = (state: RootState) => selectStudioState(state).pipelineSchema;
const getHaystackTypes = (state: RootState) => selectStudioState(state).haystackTypes;
const getComponentMap = (state: RootState) => selectStudioState(state).componentMap;
const getFetchPipelineSchemaStatus = (state: RootState) =>
  selectStudioState(state).fetchPipelineSchemaStatus;
const getFetchPipelineInputOutputsStatus = (state: RootState) =>
  selectStudioState(state).fetchPipelineInputOutputsStatus;
const getInputOutputMap = (state: RootState) => selectStudioState(state).inputOutputMap;
const getDocumentStoreComponents = (state: RootState) =>
  selectStudioState(state).documentStoreComponents;
const getIsYamlView = (state: RootState) => selectStudioState(state).isYamlView;
const getExportCodeModalOpen = (state: RootState) => selectStudioState(state).exportCodeModalOpen;
const getYaml = (state: RootState) => selectStudioState(state).yaml;
const getFetchedSingleIntegratedPipelineYaml = (state: RootState) =>
  selectStudioState(state).fetchedSingleIntegratedPipelineYaml;
const getIsReadOnly = (state: RootState) => selectStudioState(state).isReadOnly;
const getShowReadOnlyWarningModal = (state: RootState) =>
  selectStudioState(state).showReadOnlyWarningModal;
const getHoveredNodeId = (state: RootState) => selectStudioState(state).hoveredNodeId;
const getReactFlowInstance = (state: RootState) => selectStudioState(state).reactFlowInstance;
const getPastIndexingReactFlowState = (state: RootState) =>
  selectStudioState(state).pastIndexingReactFlowState;
const getFutureIndexingReactFlowState = (state: RootState) =>
  selectStudioState(state).futureIndexingReactFlowState;
const getPastQueryReactFlowState = (state: RootState) =>
  selectStudioState(state).pastQueryReactFlowState;
const getFutureQueryReactFlowState = (state: RootState) =>
  selectStudioState(state).futureQueryReactFlowState;
const getNewNodeAdded = (state: RootState) => selectStudioState(state).newNodeAdded;
const getNodeParamModalVisible = (state: RootState) =>
  selectStudioState(state).nodeParamModalVisible;
const getDisconnectedDocumentStores = (state: RootState) =>
  selectStudioState(state).disconnectedDocumentStores;
const getSaveStudioPipelineStatus = (state: RootState) =>
  selectStudioState(state).saveStudioPipelineStatus;
const getIsIndexPipeline = (state: RootState) => selectStudioState(state).isIndexPipeline;
const getComponentPositions = (state: RootState) => selectStudioState(state).componentPositions;
const getFetchPipelineVisualizationDataStatus = (state: RootState) =>
  selectStudioState(state).fetchPipelineVisualizationDataStatus;

// createSelector
export const selectPipelineSchema = createSelector(
  [getPipelineSchema],
  (pipelineSchema) => pipelineSchema,
);
export const selectHaystackTypes = createSelector(
  [getHaystackTypes],
  (haystackTypes) => haystackTypes,
);
export const selectComponentMap = createSelector([getComponentMap], (componentMap) => componentMap);
export const selectInputOutputMap = createSelector(
  [getInputOutputMap],
  (inputOutputMap) => inputOutputMap,
);
export const selectDocumentStoreComponents = createSelector(
  [getDocumentStoreComponents],
  (documentStoreComponents) => documentStoreComponents,
);
export const selectFetchPipelineSchemaStatus = createSelector(
  [getFetchPipelineSchemaStatus],
  (fetchPipelineSchemaStatus) => fetchPipelineSchemaStatus,
);
export const selectFetchPipelineInputOutputsStatus = createSelector(
  [getFetchPipelineInputOutputsStatus],
  (fetchPipelineInputOutputsStatus) => fetchPipelineInputOutputsStatus,
);
export const selectIsYamlView = createSelector([getIsYamlView], (isYamlView) => isYamlView);
export const selectExportCodeModalOpen = createSelector(
  [getExportCodeModalOpen],
  (exportCodeModalOpen) => exportCodeModalOpen,
);
export const selectYaml = createSelector([getYaml], (yaml) => yaml);
export const selectFetchedSingleIntegratedPipelineYaml = createSelector(
  [getFetchedSingleIntegratedPipelineYaml],
  (fetchedSingleIntegratedPipelineYaml) => fetchedSingleIntegratedPipelineYaml,
);
export const selectIsReadOnly = createSelector([getIsReadOnly], (isReadOnly) => isReadOnly);
export const selectShowReadOnlyWarningModal = createSelector(
  [getShowReadOnlyWarningModal],
  (showReadOnlyWarningModal) => showReadOnlyWarningModal,
);
export const selectHoveredNodeId = createSelector(
  [getHoveredNodeId],
  (hoveredNodeId) => hoveredNodeId,
);
export const selectReactFlowInstance = createSelector(
  [getReactFlowInstance],
  (reactFlowInstance) => reactFlowInstance,
);
export const selectPastIndexingReactFlowState = createSelector(
  [getPastIndexingReactFlowState],
  (pastIndexingReactFlowState) => pastIndexingReactFlowState,
);
export const selectFutureIndexingReactFlowState = createSelector(
  [getFutureIndexingReactFlowState],
  (futureIndexingReactFlowState) => futureIndexingReactFlowState,
);
export const selectPastQueryReactFlowState = createSelector(
  [getPastQueryReactFlowState],
  (pastQueryReactFlowState) => pastQueryReactFlowState,
);
export const selectFutureQueryReactFlowState = createSelector(
  [getFutureQueryReactFlowState],
  (futureQueryReactFlowState) => futureQueryReactFlowState,
);
export const selectNewNodeAdded = createSelector([getNewNodeAdded], (newNodeAdded) => newNodeAdded);
export const selectNodeParamModalVisible = createSelector(
  [getNodeParamModalVisible],
  (nodeParamModalVisible) => nodeParamModalVisible,
);
export const selectDisconnectedDocumentStores = createSelector(
  [getDisconnectedDocumentStores],
  (disconnectedDocumentStores) => disconnectedDocumentStores,
);
export const selectSaveStudioPipelineStatus = createSelector(
  [getSaveStudioPipelineStatus],
  (saveStudioPipelineStatus) => saveStudioPipelineStatus,
);
export const selectIsIndexPipeline = createSelector(
  [getIsIndexPipeline],
  (isIndexPipeline) => isIndexPipeline,
);
export const selectComponentPositions = createSelector(
  [getComponentPositions],
  (componentPositions) => componentPositions,
);
export const selectFetchPipelineVisualizationDataStatus = createSelector(
  [getFetchPipelineVisualizationDataStatus],
  (fetchPipelineVisualizationDataStatus) => fetchPipelineVisualizationDataStatus,
);
